import { cloneDeep } from "lodash";
import { NDataTable, NTooltip, PaginationInfo } from "naive-ui";
import { FilterState, SortState } from "naive-ui/es/data-table/src/interface";
import { mapState } from "pinia";
import { defineComponent, h } from "vue";
import { Station } from "~/api/models/station.model";
import componentBase from "~/components/component-base";
import { useStationStore } from "~/store/station.store";
import { DispatchDeviceTableRow } from "./row.model";
import { getStationAlarm } from "~/misc/station-utils";
import { NetworkIndicator } from "~/components/device-list-item/device-list-item";
import { AvoltaAlarm, AvoltaAlarmEnum } from "~/configs/alarms/avolta-alarm.model";
import { redColor, warningColor } from "~/variables";
import { useDispatchStore } from "~/store/dispatch.store";
import moment from "moment";


export default defineComponent({

    extends: componentBase,

    components: {
        NDataTable,
        NTooltip,
    },

    data() {
        return {
            networkRepresentation: {
                '-1': { value: -1, icon: 'elichens-avolta elichens-signal-0', label: this.$t('global.network.offline'), tooltip: ''},
                '0': { value: 0, icon: 'elichens-avolta elichens-signal-1', label: this.$t('global.network.very_poor'), tooltip: ''},
                '1': { value: 1, icon: 'elichens-avolta elichens-signal-2', label: this.$t('global.network.poor'), tooltip: ''},
                '2': { value: 2, icon: 'elichens-avolta elichens-signal-2', label: this.$t('global.network.average'), tooltip: ''},
                '3': { value: 3, icon: 'elichens-avolta elichens-signal-3', label: this.$t('global.network.good'), tooltip: ''},
                '4': { value: 4, icon: 'elichens-avolta elichens-signal-4', label: this.$t('global.network.very_good'), tooltip: ''},
                '5': { value: 5, icon: 'elichens-avolta elichens-signal-4', label: this.$t('global.network.very_good'), tooltip: ''},
            } as {[value: number]: NetworkIndicator },
            loading: false,
            columns: [
                {
                    key: 'status',
                    title: '',
                    width: 50,
                    render: (row: DispatchDeviceTableRow) => {
                        if(row?.alarms && row?.alarms.name === AvoltaAlarmEnum.CH4_concentration_alarm) {
                            return h('span', { class: "mdi mdi-alert has-text-danger is-size-4" } );
                        } else if(row?.alarms) {
                            return h('span', { class: "mdi mdi-alert has-text-warning is-size-4" } );
                        } else {
                            return h('span', { class: "mdi mdi-check-circle has-text-success is-size-4" } );
                        }
                    }
                },
                {
                    key: 'serial',
                    width: 100,
                    title: this.$t('dispatch.device_datatable.columns.serial.label'),
                },
                {
                    key: 'meterSN',
                    width: 120,
                    title: this.$t('dispatch.device_datatable.columns.meter_sn.label')
                },
                {
                    key: 'location',
                    title: this.$t('dispatch.device_datatable.columns.address.label'),
                },
                {
                    key: 'events',
                    title: this.$t('dispatch.device_datatable.columns.events.label'),
                    width: 250,
                    render: (row: DispatchDeviceTableRow) => {
                        const alarm = getStationAlarm(row.station);

                        if(!alarm) {
                            return h('span');
                        }

                        return h(
                            NTooltip, 
                            { trigger: 'hover'},
                            {
                                trigger: () => h('div', {class: 'dispatch-device-column events'}, [
                                    h(
                                        'span', 
                                        { class: `is-size-4 ${alarm?.icon} mr-3`, style: { color: alarm?.name === AvoltaAlarmEnum.CH4_concentration_alarm ? redColor : warningColor }}
                                    ),
                                    h(
                                        'span',
                                       row.since?.format('LLL')
                                    ),
                                ]), 
                                default: () => `${alarm.short}`,
                            }
                        );
                    }
                },
                {
                    key: 'lel',
                    title: this.$t('dispatch.device_datatable.columns.lel.label'),
                    width: 80,
                    render: (row: DispatchDeviceTableRow) => {

                        if(!row.alarms || row.alarms.name !== AvoltaAlarmEnum.CH4_concentration_alarm) {
                            return h('span', {class: ''}, row.lel);
                        }

                        if(row.lel === row.lastLel) {
                            return h('span', null, [
                                h('span', {class: 'has-text-warning mr-1'}, row.lel),
                                h('span', {class: 'mdi mdi-arrow-right has-text-warning is-size-6'}),
                            ]);
                        } else if(row.lastLel > row.lel) {
                            return h('span', null, [
                                h('span', {class: 'has-text-success mr-1'}, row.lel),
                                h('span', {class: 'mdi mdi-arrow-bottom-right has-text-success is-size-6'}),
                            ]);
                        } else if(row.lastLel < row.lel) {
                            return h('span', null, [
                                h('span', {class: 'has-text-danger mr-1'}, row.lel),
                                h('span', {class: 'mdi mdi-arrow-top-right has-text-danger is-size-6'}),
                            ]);
                        } else {
                            return h('span', null, [
                                h('span', {class: 'has-text-warning mr-1'}, row.lel),
                                h('span', {class: 'mdi mdi-arrow-right is-size-6'}),
                            ]);
                        }
                    },
                },
                {
                    key: 'network',
                    title: this.$t('dispatch.device_datatable.columns.events.network'),
                    width: 80,
                    render: (row: DispatchDeviceTableRow) => {
                        return h(
                            NTooltip,
                            {
                                trigger: 'hover',
                            },
                            {
                                trigger: () => {
                                    return h('span', { class: 'is-size-5 ' + row.network.icon }, '')
                                },
                                default: () => {
                                    return [
                                        h('span', { class: 'has-text-weight-semibold'}, this.$t('station_table.quality') + ' '),
                                        h('span', null, row.network?.label || ''),
                                    ]
                                },
                            }
                        );
                    }
                }
            ],
            data: [] as DispatchDeviceTableRow [],
            pagination: {
                page: 1,
                pageCount: Math.floor(this.stations?.length || 0) / 10 + 1,
                pageSize: 6,       
            } as PaginationInfo,
            filter: null as FilterState | null,
            sorter: null as SortState | null,

        }
    },

    computed: {
        ...mapState(useDispatchStore, ['stations', 'measures', 'events', 'ready']),
        ...mapState(useStationStore, ['group']),

        rows(): DispatchDeviceTableRow[] {
            return this.stations.map(s => {

                let network = this.networkRepresentation['4'];

                if(s?.latest_values['network_quality']) {
                    network = this.networkRepresentation[s?.latest_values['network_quality']];
                }

                const lastLel: number = this.measures.length ? this.measures[s?.id][0]?.values?.CH4_lel : 0;
                
                const alarm: AvoltaAlarm = getStationAlarm(s);
                const alarmEvent = this.events[s?.id]?.find(e => e.type === alarm?.name && e.level === 'warning');

                let since = moment();
                if(alarm?.name === AvoltaAlarmEnum.offline) {
                    since = moment.unix(s?.last_seen);
                } else if(alarmEvent){
                    since = moment.unix(alarmEvent?.date / 1000);
                }

                return {
                    id: s?.id,
                    station: s,
                    location: s?.metadata?.['elichens/public/address'],
                    meterSN: Math.random().toString(16).slice(5).toUpperCase(),
                    lastLel: lastLel,
                    alarms: getStationAlarm(s),
                    since,
                    lel: s?.latest_values?.CH4_lel,
                    serial: s?.serial,
                    network,
                } as DispatchDeviceTableRow;
            });
        }
    },

    methods: {
        goToStationDetails(station: Station): void {
            this.$router.push({ name: 'station-details', params: { station: station.id } })
        },

        handleSorterChange(sortState: SortState) {
            this.sorter = sortState;
            this.query(1, this.pagination.pageSize);
        },

        handleFiltersChange(filterState: FilterState) {
            this.filter = filterState;
            this.query(1, this.pagination.pageSize);
        },

        handlePageChange(page) {
            this.query(page, this.pagination.pageSize);
        },

        query(page: number, pageSize = 6) {
            const skip = (page - 1) * pageSize;
            const top = pageSize;
            let rows: DispatchDeviceTableRow[] = cloneDeep(this.rows);
            // if(this.filter && (this.filter.type as FilterOptionValue[]).length) {
            //     rows = rows.filter((row) => (this.filter.type as FilterOptionValue[]).includes(row.type));
            // }

            // rows = rows.sort((m1, m2) => m2.date - m1.date);

            // if(this.sorter && this.sorter.columnKey === 'date' && this.sorter.order === 'descend') {
            //     rows = rows.sort().reverse();
            // }

            this.pagination.pageCount = Math.ceil(rows.length / this.pagination.pageSize);
            rows = rows.slice(skip, skip + top);

            this.data = rows;

            this.pagination.page = page;
        },

        getAddress(station: Station) {
            return station?.metadata?.['elichens/public/address'];
        },

        onTableClick(deviceId: string) {
            this.$router.push({ name: 'station-details', params: { group: this.group?.name, station: deviceId}})
        }

    },

    watch: {
        ready() {
            this.query(1, this.pagination.pageSize);
        },
    }
})